import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, Navigate, useSearchParams } from "react-router-dom";
import { Lock, Mail, Loader2, KeyRound, ArrowLeft } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { toast } from "sonner";

// Biconcave disc cells drifting over the hero image
const CELLS = [
  { id: 1,  x: 10, y: 18, r: 26, anim: "a", dur: 15, delay:  0,   op: 0.20 },
  { id: 2,  x: 38, y: 72, r: 16, anim: "b", dur: 20, delay: -6,   op: 0.15 },
  { id: 3,  x: 62, y: 12, r: 30, anim: "c", dur: 13, delay: -3,   op: 0.13 },
  { id: 4,  x: 80, y: 50, r: 18, anim: "a", dur: 17, delay: -9,   op: 0.18 },
  { id: 5,  x: 20, y: 88, r: 22, anim: "b", dur: 19, delay: -2,   op: 0.16 },
  { id: 6,  x: 68, y: 38, r: 13, anim: "c", dur: 23, delay: -7,   op: 0.12 },
  { id: 7,  x: 48, y: 93, r: 24, anim: "a", dur: 16, delay: -11,  op: 0.14 },
  { id: 8,  x: 90, y: 10, r: 19, anim: "b", dur: 18, delay: -4,   op: 0.19 },
  { id: 9,  x:  4, y: 58, r: 11, anim: "c", dur: 25, delay: -8,   op: 0.17 },
  { id: 10, x: 53, y: 30, r: 28, anim: "a", dur: 14, delay: -1,   op: 0.11 },
  { id: 11, x: 84, y: 80, r: 17, anim: "b", dur: 21, delay: -5,   op: 0.18 },
  { id: 12, x: 28, y: 45, r: 21, anim: "c", dur: 16, delay: -13,  op: 0.15 },
  { id: 13, x: 73, y: 65, r: 14, anim: "a", dur: 22, delay: -10,  op: 0.13 },
  { id: 14, x: 16, y: 35, r: 20, anim: "b", dur: 12, delay: -15,  op: 0.17 },
  { id: 15, x: 44, y: 55, r: 15, anim: "c", dur: 18, delay: -7,   op: 0.14 },
];

function BloodCellField() {
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
      {CELLS.map((c) => (
        <div
          key={c.id}
          style={{
            position: "absolute",
            left: `${c.x}%`,
            top: `${c.y}%`,
            width: c.r * 2,
            height: c.r * 2,
            borderRadius: "50%",
            /* biconcave disc: rim visible, centre lighter */
            background: `radial-gradient(circle at 38% 36%,
              rgba(255,255,255,0.55) 0%,
              rgba(255,255,255,0.18) 32%,
              transparent 60%,
              rgba(255,255,255,${c.op}) 100%)`,
            border: `1px solid rgba(255,255,255,${c.op * 0.9})`,
            "--cell-opacity": c.op,
            animation: `cell-drift-${c.anim} ${c.dur}s ${c.delay}s ease-in-out infinite`,
          }}
        />
      ))}
    </div>
  );
}

const loginSchema = z.object({
  email: z
    .string()
    .min(1, "Email is required")
    .email("Enter a valid email address")
    .max(254),
  password: z.string().min(1, "Password is required").max(200),
});

const mfaSchema = z.object({
  code: z
    .string()
    .min(4, "Enter the code from your authenticator")
    .max(40, "Code is too long"),
});

export default function Login() {
  const { user, login, loginMfa } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const idleToastFired = useRef(false);

  const passwordForm = useForm({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: "", password: "" },
    mode: "onBlur",
  });

  const mfaForm = useForm({
    resolver: zodResolver(mfaSchema),
    defaultValues: { code: "" },
    mode: "onBlur",
  });

  const [mfaToken, setMfaToken] = useState("");
  const [mfaIsBackup, setMfaIsBackup] = useState(false);
  const [serverError, setServerError] = useState("");
  const codeInputRef = useRef(null);

  useEffect(() => {
    if (params.get("reason") === "idle" && !idleToastFired.current) {
      idleToastFired.current = true;
      toast.error("Signed out due to inactivity. Please sign in again.");
    }
  }, [params]);

  useEffect(() => {
    if (mfaToken && codeInputRef.current) codeInputRef.current.focus();
  }, [mfaToken, mfaIsBackup]);

  if (user) return <Navigate to="/" replace />;

  const submitPassword = async ({ email, password }) => {
    setServerError("");
    try {
      const result = await login(email.trim(), password);
      if (result.kind === "mfa") {
        setMfaToken(result.mfa_token);
        toast.message("Enter your two-factor code");
      } else {
        toast.success("Welcome back");
        navigate("/", { replace: true });
      }
    } catch (err) {
      const msg = formatApiError(err);
      setServerError(msg);
      toast.error(msg);
    }
  };

  const submitMfa = async ({ code }) => {
    setServerError("");
    try {
      await loginMfa(mfaToken, code.trim());
      toast.success("Welcome back");
      navigate("/", { replace: true });
    } catch (err) {
      const msg = formatApiError(err);
      setServerError(msg);
      toast.error(msg);
      mfaForm.setValue("code", "");
      if (/sign in again/i.test(msg)) {
        setMfaToken("");
      }
    }
  };

  const cancelMfa = () => {
    setMfaToken("");
    mfaForm.reset({ code: "" });
    setMfaIsBackup(false);
    setServerError("");
  };

  const quickFill = (em, pw) => {
    passwordForm.setValue("email", em, { shouldValidate: false });
    passwordForm.setValue("password", pw, { shouldValidate: false });
    passwordForm.clearErrors();
  };

  const passwordSubmitting = passwordForm.formState.isSubmitting;
  const mfaSubmitting = mfaForm.formState.isSubmitting;
  const mfaCodeValue = mfaForm.watch("code");

  return (
    <div className="min-h-screen w-full flex bg-cream relative overflow-hidden">
      {/* Mobile-only full-screen hero — animated blood cells */}
      <video
        src="/brand/cells-flow.mp4"
        autoPlay
        loop
        muted
        playsInline
        aria-hidden="true"
        className="lg:hidden absolute inset-0 w-full h-full object-cover object-center z-0"
        data-testid="login-hero-mobile-bg"
      />
      <div
        className="lg:hidden absolute inset-0 z-0 pointer-events-none bg-gradient-to-br from-brand-dark/35 via-brand/15 to-brand-dark/55"
        aria-hidden="true"
      />
      <div className="lg:hidden absolute inset-0 z-0">
        <BloodCellField />
      </div>

      {/* Desktop hero panel — animated blood cells */}
      <div className="relative hidden lg:block lg:flex-1 overflow-hidden">
        <video
          src="/brand/cells-flow.mp4"
          autoPlay
          loop
          muted
          playsInline
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover object-center"
          data-testid="login-hero-image"
        />
        <div className="absolute inset-0 bg-brand-dark/10 pointer-events-none" />
        <BloodCellField />
      </div>

      <div className="relative z-10 flex-1 flex items-center justify-center px-4 py-8 lg:px-6 lg:py-10">
        <div className="w-full max-w-md bg-white/95 backdrop-blur-sm shadow-xl rounded-2xl p-6 sm:p-8 lg:bg-transparent lg:backdrop-blur-none lg:shadow-none lg:rounded-none lg:p-0">
          <div className="flex justify-center mb-6">
            <img
              src="/brand/logo.png"
              alt="Constantiaberg Haematology"
              className="object-contain w-auto"
              style={{ maxWidth: "200px", maxHeight: "72px" }}
              data-testid="login-form-logo"
            />
          </div>

          <div className="text-center mb-8">
            <h2 className="font-serif text-3xl font-semibold tracking-tight text-zinc-900">
              Constantiaberg Haematology
            </h2>
            <p className="text-sm text-neutralsoft mt-1 font-sans">
              Transplant Ops Hub — clinical operations console.
            </p>
          </div>

          {!mfaToken ? (
            <>
              <div className="mb-2 text-[11px] uppercase tracking-[0.22em] text-brand font-medium">
                Sign in
              </div>
              <Form {...passwordForm}>
                <form
                  onSubmit={passwordForm.handleSubmit(submitPassword)}
                  className="mt-8 space-y-5"
                  noValidate
                  data-testid="login-form"
                >
                  <FormField
                    control={passwordForm.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-xs font-medium text-zinc-700">
                          Email
                        </FormLabel>
                        <div className="relative">
                          <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 z-10" />
                          <FormControl>
                            <Input
                              type="text"
                              autoComplete="username"
                              className="pl-9 h-10 bg-white"
                              data-testid="login-email-input"
                              {...field}
                            />
                          </FormControl>
                        </div>
                        <FormMessage data-testid="login-email-error" />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={passwordForm.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-xs font-medium text-zinc-700">
                          Password
                        </FormLabel>
                        <div className="relative">
                          <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 z-10" />
                          <FormControl>
                            <Input
                              type="password"
                              autoComplete="current-password"
                              className="pl-9 h-10 bg-white"
                              data-testid="login-password-input"
                              {...field}
                            />
                          </FormControl>
                        </div>
                        <FormMessage data-testid="login-password-error" />
                      </FormItem>
                    )}
                  />

                  {serverError && (
                    <div
                      className="rounded-lg border border-rose-200 bg-rose-50 text-rose-800 text-xs px-3 py-2"
                      data-testid="login-error"
                      role="alert"
                    >
                      {serverError}
                    </div>
                  )}

                  <Button
                    type="submit"
                    disabled={passwordSubmitting}
                    className="w-full h-10 bg-brand hover:bg-brand-dark text-white"
                    data-testid="login-submit-btn"
                  >
                    {passwordSubmitting ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      "Sign in"
                    )}
                  </Button>

                  <div className="text-center pt-1">
                    <Link
                      to="/forgot-password"
                      className="text-xs text-brand hover:text-brand-dark font-medium"
                      data-testid="forgot-password-link"
                    >
                      Forgot your password?
                    </Link>
                  </div>
                </form>
              </Form>

              {process.env.NODE_ENV !== "production" && (
              <div className="mt-8">
                <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-zinc-400 mb-3">
                  Quick access — demo accounts
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { label: "Admin", email: "admin@hub.local", pw: "Admin@123", color: "brand" },
                    { label: "Coordinator", email: "coord@hub.local", pw: "Coord@123", color: "blue" },
                    { label: "Viewer", email: "viewer@hub.local", pw: "Viewer@123", color: "zinc" },
                  ].map((q) => (
                    <button
                      key={q.email}
                      type="button"
                      onClick={() => quickFill(q.email, q.pw)}
                      data-testid={`quickfill-${q.label.toLowerCase()}-btn`}
                      className={`text-[11px] font-medium rounded-md border px-2 py-1.5 transition-colors ${
                        q.color === "brand"
                          ? "border-brand-200 bg-brand-50 text-brand-dark hover:bg-brand-100"
                          : q.color === "blue"
                          ? "border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100"
                          : "border-zinc-200 bg-zinc-50 text-zinc-700 hover:bg-zinc-100"
                      }`}
                    >
                      {q.label}
                    </button>
                  ))}
                </div>
              </div>
              )}
            </>
          ) : (
            <>
              <div className="mb-2 text-[11px] uppercase tracking-[0.22em] text-brand font-medium flex items-center gap-1.5">
                <KeyRound className="w-3 h-3" /> Two-factor authentication
              </div>
              <Form {...mfaForm}>
                <form
                  onSubmit={mfaForm.handleSubmit(submitMfa)}
                  className="mt-6 space-y-5"
                  noValidate
                  data-testid="mfa-form"
                >
                  <FormField
                    control={mfaForm.control}
                    name="code"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-xs font-medium text-zinc-700">
                          {mfaIsBackup
                            ? "Backup code"
                            : "6-digit code from your authenticator"}
                        </FormLabel>
                        <FormControl>
                          <Input
                            ref={(el) => {
                              field.ref(el);
                              codeInputRef.current = el;
                            }}
                            type="text"
                            inputMode={mfaIsBackup ? "text" : "numeric"}
                            autoComplete="one-time-code"
                            placeholder={mfaIsBackup ? "XXXX-XXXX-XXXX-XXXX" : "123 456"}
                            className="h-12 mt-1.5 bg-white font-mono text-center tracking-widest text-lg"
                            data-testid="mfa-code-input"
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            name={field.name}
                          />
                        </FormControl>
                        <FormMessage data-testid="mfa-code-error" />
                      </FormItem>
                    )}
                  />

                  {serverError && (
                    <div
                      className="rounded-lg border border-rose-200 bg-rose-50 text-rose-800 text-xs px-3 py-2"
                      data-testid="mfa-error"
                      role="alert"
                    >
                      {serverError}
                    </div>
                  )}

                  <Button
                    type="submit"
                    disabled={mfaSubmitting || !mfaCodeValue?.trim()}
                    className="w-full h-10 bg-brand hover:bg-brand-dark text-white"
                    data-testid="mfa-submit-btn"
                  >
                    {mfaSubmitting ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      "Verify & continue"
                    )}
                  </Button>

                  <div className="flex items-center justify-between text-xs">
                    <button
                      type="button"
                      onClick={cancelMfa}
                      className="text-zinc-500 hover:text-zinc-700 flex items-center gap-1"
                      data-testid="mfa-cancel-btn"
                    >
                      <ArrowLeft className="w-3 h-3" /> Back
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setMfaIsBackup(!mfaIsBackup);
                        mfaForm.reset({ code: "" });
                        setServerError("");
                      }}
                      className="text-brand hover:text-brand-dark font-medium"
                      data-testid="mfa-toggle-backup-btn"
                    >
                      {mfaIsBackup
                        ? "Use authenticator code instead"
                        : "Use a backup code"}
                    </button>
                  </div>
                </form>
              </Form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
